// SendInvite.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import theme from '../styles/theme';

const { colors, fonts } = theme;

/**
 * Two modes in one component:
 * - Sender mode (default): send an invite.
 * - Receiver mode: pass inviteId to preview with Accept/Decline.
 *
 * Props:
 *   receiverId?: string         // required in Sender mode
 *   defaultProject?: string     // optional in Sender mode
 *   inviteId?: string           // when present, switches to Receiver mode
 */
const SendInvite = ({ receiverId, defaultProject = '', inviteId }) => {
  /* ---------------------------- Common State ---------------------------- */
  const [sending, setSending] = useState(false);

  /* ---------------------------- Receiver Mode --------------------------- */
  const isReceiverMode = Boolean(inviteId);

  const [viewerId, setViewerId] = useState(null);
  const [invite, setInvite] = useState(null);
  const [project, setProject] = useState(null);
  const [role, setRole] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [ndaChecked, setNdaChecked] = useState(false);
  const [processingDecision, setProcessingDecision] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(isReceiverMode);

  useEffect(() => {
    if (!isReceiverMode) return;

    (async () => {
      setLoadingPreview(true);

      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id || null;
      setViewerId(uid);

      const { data: inv } = await supabase
        .from('collaboration_invites')
        .select('*')
        .eq('id', inviteId)
        .single();

      if (!inv) {
        setInvite(null);
        setLoadingPreview(false);
        return;
      }
      setInvite(inv);

      if (uid !== inv.receiver_id && uid !== inv.sender_id) {
        setLoadingPreview(false);
        return;
      }

      const { data: proj } = await supabase
        .from('projects')
        .select('*')
        .eq('id', inv.project_id)
        .single();
      setProject(proj || null);

      const { data: roles } = await supabase
        .from('project_roles')
        .select('*')
        .eq('project_id', inv.project_id)
        .limit(1);
      setRole(roles?.[0] || null);

      const { data: escrow } = await supabase
        .from('escrow_accounts')
        .select('id')
        .eq('project_id', inv.project_id)
        .single();

      if (escrow?.id) {
        const { data: ms } = await supabase
          .from('milestones')
          .select('*')
          .eq('escrow_id', escrow.id)
          .order('due_date', { ascending: true });
        setMilestones(ms || []);
      } else {
        setMilestones([]);
      }

      setLoadingPreview(false);
    })();
  }, [inviteId, isReceiverMode]);

  /* ----------------------------- Sender Mode ---------------------------- */
  const [inviteForm, setInviteForm] = useState({ project: defaultProject, role: '', message: '' });
  const [userProjects, setUserProjects] = useState([]);
  const [agreed, setAgreed] = useState(false);

  // NEW: receiver’s creator_profile.id (milestones.collaborator_id references this)
  const [receiverProfileId, setReceiverProfileId] = useState(null);

  // NEW: proactive guard state for selected project
  const [guardLoading, setGuardLoading] = useState(false);
  const [guardBlocked, setGuardBlocked] = useState(false);
  const [guardMsg, setGuardMsg] = useState('');

  // NEW: success banner (after sending)
  const [sentBanner, setSentBanner] = useState(null); // { projectTitle, role, text }

  useEffect(() => {
    if (isReceiverMode) return; // only sender mode

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const sender_id = session?.user?.id;
      if (!sender_id) return;

      const { data: projects, error } = await supabase
        .from('projects')
        .select('id, title')
        .eq('creator_id', sender_id)
        .not('status', 'eq', 'Completed');

      if (!error) setUserProjects(projects || []);
    })();
  }, [isReceiverMode]);

  // NEW: resolve the receiver’s creator_profile once
  useEffect(() => {
    if (isReceiverMode || !receiverId) return;
    (async () => {
      const { data } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', receiverId)
        .single();
      setReceiverProfileId(data?.id || null);
    })();
  }, [receiverId, isReceiverMode]);

  // NEW: evaluate guard whenever project or receiverProfileId changes
  useEffect(() => {
    if (isReceiverMode) return;

    const evaluateInviteGuard = async (projectId) => {
      setGuardLoading(true);
      setGuardBlocked(false);
      setGuardMsg('');

      if (!projectId) {
        setGuardLoading(false);
        return;
      }

      try {
        // Look up escrow for the selected project
        const { data: escrow } = await supabase
          .from('escrow_accounts')
          .select('id')
          .eq('project_id', projectId)
          .single();

        if (!escrow?.id) {
          // No escrow → nothing assigned yet, allow invite
          setGuardLoading(false);
          setGuardBlocked(false);
          setGuardMsg('');
          return;
        }

        // Fetch assigned milestones (non-null collaborator)
        const { data: assignedMs } = await supabase
          .from('milestones')
          .select('collaborator_id')
          .eq('escrow_id', escrow.id)
          .not('collaborator_id', 'is', null);

        const assignedTo = Array.from(
          new Set((assignedMs || []).map((m) => m.collaborator_id).filter(Boolean))
        );

        if (assignedTo.length === 0) {
          // No one assigned → allow
          setGuardBlocked(false);
          setGuardMsg('');
        } else {
          // Someone(s) assigned — allow only if all assigned to the same receiver
          const invitingSamePerson =
            receiverProfileId && assignedTo.length === 1 && assignedTo[0] === receiverProfileId;

          if (!invitingSamePerson) {
            setGuardBlocked(true);
            setGuardMsg('This project already has milestones assigned to a different collaborator. Your current plan allows one active collaborator per project. Next step: create a separate project (with its own milestones) for this collaborator, then send the invite.');
          } else {
            setGuardBlocked(false);
            setGuardMsg('');
          }
        }
      } catch (e) {
        // On errors, don’t silently allow; show a soft message and block sending
        setGuardBlocked(true);
        setGuardMsg('We could not verify assignment for this project right now.');
      } finally {
        setGuardLoading(false);
      }
    };

    evaluateInviteGuard(inviteForm.project);
  }, [inviteForm.project, receiverProfileId, isReceiverMode]);

  const sendInvite = async () => {
    setSending(true);
    setSentBanner(null); // clear any previous success banner

    const { data: { session } } = await supabase.auth.getSession();
    const sender_id = session?.user?.id;

    if (!sender_id || !receiverId) {
      setSending(false);
      return;
    }
    if (!inviteForm.project || !inviteForm.role) {
      setSending(false);
      return;
    }
    if (!agreed) {
      setSending(false);
      return;
    }
    // Guard: block with banner already visible above
    if (guardBlocked) {
      setSending(false);
      return;
    }

    // Insert into collaboration_invites (unchanged)
    const { error: inviteError } = await supabase.from('collaboration_invites').insert({
      sender_id,
      receiver_id: receiverId,
      project_id: inviteForm.project,
      role: inviteForm.role,
      message: inviteForm.message
    });

    // Also log an agreement row (unchanged in your code)
    const { error: agreementError } = await supabase.from('agreements').insert({
      user_id: sender_id,
      target_user_id: receiverId,
      project_id: inviteForm.project,
      type: 'collab_invite_acceptance',
      agreed: true
    });

    if (!inviteError && !agreementError) {
      // Build a friendly banner BEFORE resetting the form
      const projectTitle =
        (userProjects.find((p) => p.id === inviteForm.project)?.title) || 'Selected project';

      setSentBanner({
        projectTitle,
        role: inviteForm.role,
        text: (inviteForm.message || '').trim()
      });

      // Reset form on success
      setInviteForm({ project: '', role: '', message: '' });
      setAgreed(false);
      setGuardBlocked(false);
      setGuardMsg('');
    }
    setSending(false);
  };

  /* ------------------------- Receiver Actions --------------------------- */
  const totalMilestoneBudget = (milestones || []).reduce((s, m) => s + (Number(m.amount) || 0), 0);

  const acceptInvite = async () => {
    if (!viewerId || !invite) return;
    if (project?.nda_required && !ndaChecked) return;
    setProcessingDecision(true);
    await supabase.from('agreements').insert({
      user_id: viewerId,
      target_user_id: invite.sender_id,
      project_id: invite.project_id,
      type: 'invite_accepted',
      agreed: true,
      metadata: { invite_id: invite.id, role: invite.role }
    });
    setProcessingDecision(false);
  };

  const declineInvite = async () => {
    if (!viewerId || !invite) return;
    setProcessingDecision(true);
    await supabase.from('agreements').insert({
      user_id: viewerId,
      target_user_id: invite.sender_id,
      project_id: invite.project_id,
      type: 'invite_declined',
      agreed: false,
      metadata: { invite_id: invite.id, role: invite.role }
    });
    setProcessingDecision(false);
  };

  /* ----------------------------- Styling -------------------------------- */
  const inputStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.inputBorder}`,
    color: colors.textPrimary,
    borderRadius: '6px',
    marginBottom: '12px',
    fontFamily: fonts.body
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500',
    color: colors.textSecondary,
    marginBottom: '6px',
    display: 'block'
  };

  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: colors.accent,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: fonts.body
  };

  const Box = ({ title, children }) => (
    <section style={{
      backgroundColor: colors.cardBackground,
      padding: '16px',
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      marginBottom: 16
    }}>
      {title && <h4 style={{ margin: '0 0 10px', color: colors.accent }}>{title}</h4>}
      {children}
    </section>
  );

  /* ----------------------------- Render --------------------------------- */
  if (isReceiverMode) {
    // Receiver (Preview + Accept/Decline)
    return (
      <div style={{
        marginTop: '16px',
        backgroundColor: colors.cardBackground,
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.05)',
        fontFamily: fonts.body
      }}>
        <h3 style={{ marginTop: 0, marginBottom: 12, color: colors.accent }}>🎬 Project Offer</h3>

        {loadingPreview && <div>Loading…</div>}
        {!loadingPreview && !invite && <div>Invite not found.</div>}
        {!loadingPreview && invite && viewerId !== invite.receiver_id && viewerId !== invite.sender_id && (
          <div>Not authorized to view this offer.</div>
        )}

        {!loadingPreview && invite && (viewerId === invite.receiver_id || viewerId === invite.sender_id) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16 }}>
            {/* LEFT: Content */}
            <div>
              <Box>
                <h2 style={{ margin: '0 0 6px' }}>{project?.title || 'Untitled Project'}</h2>
                <div style={{ opacity: 0.8, fontSize: 13, marginBottom: 8 }}>
                  Invited role: <strong>{invite.role || role?.role_type || 'Role'}</strong>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                  {project?.nda_required && !ndaChecked
                    ? 'This project requires an NDA. Check the NDA box to reveal full details.'
                    : (project?.description || 'No description provided.')}
                </div>
              </Box>

              <Box title="Responsibilities">
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {project?.nda_required && !ndaChecked ? 'Hidden until NDA is accepted.' : (role?.responsibilities || '—')}
                </div>
              </Box>

              <Box title="Deliverables">
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {project?.nda_required && !ndaChecked ? 'Hidden until NDA is accepted.' : (role?.deliverables || '—')}
                </div>
              </Box>
            </div>

            {/* RIGHT: Facts & Decision */}
            <div>
              <Box title="Offer Summary">
                <div style={{ fontSize: 14, display: 'grid', gap: 8 }}>
                  <div><strong>Role:</strong> {invite.role || role?.role_type || '—'}</div>
                  <div><strong>Budget Type:</strong> {role?.budget_type || '—'}</div>
                  {role?.budget_type === 'Fixed' ? (
                    <div><strong>Compensation:</strong> Fixed via milestones</div>
                  ) : (
                    <div>
                      <strong>Rate Range:</strong>{' '}
                      {(Number(role?.rate_min) || 0).toFixed(2)} – {(Number(role?.rate_max) || 0).toFixed(2)}
                    </div>
                  )}
                  {role?.due_date && <div><strong>Role Due:</strong> {role.due_date}</div>}
                  <div><strong>Project Status:</strong> {project?.status || '—'}</div>
                  <div><strong>Privacy:</strong> {project?.privacy || '—'}</div>
                </div>
              </Box>

              <Box title="Payment Plan (Milestones)">
                {(milestones || []).length ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {milestones.map((m) => (
                      <div key={m.id} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '10px 12px',
                        border: '1px dashed rgba(255,255,255,0.15)',
                        borderRadius: 10,
                        opacity: m.milestone_status === 'Completed' ? 0.7 : 1,
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{m.title || 'Milestone'}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>
                            {m.due_date ? `Due: ${m.due_date}` : 'No due date'}
                            {' • '}
                            Status: {m.milestone_status || 'Not Completed'}
                          </div>
                          {project?.nda_required && !ndaChecked ? (
                            <div style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>Description hidden (NDA)</div>
                          ) : (
                            m.description && (
                              <div style={{ fontSize: 12, marginTop: 6, opacity: 0.9, whiteSpace: 'pre-wrap' }}>
                                {m.description}
                              </div>
                            )
                          )}
                        </div>
                        <div style={{ fontWeight: 700 }}>${Number(m.amount || 0).toFixed(2)}</div>
                      </div>
                    ))}
                    <div style={{ textAlign: 'right', fontWeight: 800, marginTop: 6 }}>
                      Total: ${totalMilestoneBudget.toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <div>No milestones defined yet.</div>
                )}
              </Box>

              <Box title="Review & Accept">
                {project?.budget_mode === 'global' && (
                  <div style={{
                    marginBottom: 12, padding: 10, borderRadius: 8,
                    background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.45)'
                  }}>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <strong>Global Budget:</strong> ${Number(project?.global_budget || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      Milestones total: ${totalMilestoneBudget.toFixed(2)}
                    </div>
                  </div>
                )}

                {project?.nda_required && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, marginBottom: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={ndaChecked}
                      onChange={(e) => setNdaChecked(e.target.checked)}
                    />
                    I agree to the <a href="/legal/nda.html" target="_blank" rel="noreferrer">Mutual NDA</a>
                  </label>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                  <input type="checkbox" id="tcs" />
                  I agree to the <a href="/legal/terms.html" target="_blank" rel="noreferrer">Terms & Conditions</a>
                </label>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={acceptInvite}
                    disabled={processingDecision || (project?.nda_required && !ndaChecked)}
                    style={{
                      ...buttonStyle,
                      backgroundColor: colors.accent,
                      opacity: processingDecision ? 0.7 : 1,
                      cursor: processingDecision ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {processingDecision ? 'Processing…' : 'Accept & Continue'}
                  </button>
                  <button
                    onClick={declineInvite}
                    disabled={processingDecision}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: 'transparent',
                      color: colors.textPrimary,
                      border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: '6px',
                      cursor: processingDecision ? 'not-allowed' : 'pointer',
                      fontFamily: fonts.body,
                      opacity: processingDecision ? 0.7 : 1
                    }}
                  >
                    Decline
                  </button>
                </div>
              </Box>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Sender (Invite form) with proactive guard
  return (
    <div style={{
      marginTop: '32px',
      backgroundColor: colors.cardBackground,
      padding: '24px',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      border: '1px solid rgba(255,255,255,0.05)',
      fontFamily: fonts.body
    }}>
      <h4 style={{ marginBottom: '12px', color: colors.accent }}>🤝 Invite to Collaborate</h4>

      {/* ✅ Success banner after sending */}
      {sentBanner && (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(34,197,94,0.12)',      // green-ish
            border: '1px solid rgba(34,197,94,0.45)',
            color: '#d1fae5',
            fontSize: 13
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
            <div>
              <strong>Invite sent.</strong>{' '}
              {sentBanner.role && <>Role: <em>{sentBanner.role}</em>. </>}
              {sentBanner.projectTitle && <>Project: <em>{sentBanner.projectTitle}</em>.</>}
              {sentBanner.text && (
                <div style={{ marginTop: 4, opacity: 0.95 }}>
                  Message: <em>“{sentBanner.text}”</em>
                </div>
              )}
            </div>
            <button
              onClick={() => setSentBanner(null)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#e5e7eb',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <label style={labelStyle}>Project Title</label>
      <select
        value={inviteForm.project}
        onChange={e => {
          setInviteForm({ ...inviteForm, project: e.target.value });
          setSentBanner(null);
        }}
        style={inputStyle}
      >
        <option value="">-- Select an Active Project --</option>
        {userProjects.map(p => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>

      {/* Proactive guard message */}
      {(guardLoading || guardBlocked || guardMsg) && (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: guardBlocked ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.10)',
            border: `1px solid ${guardBlocked ? 'rgba(239,68,68,0.45)' : 'rgba(59,130,246,0.35)'}`,
            color: guardBlocked ? '#fecaca' : '#c7d2fe',
            fontSize: 13
          }}
        >
          {guardLoading ? 'Checking assignment…' : (guardMsg || 'Project looks good.')}
        </div>
      )}

      <label style={labelStyle}>Role</label>
      <input
        type="text"
        value={inviteForm.role}
        onChange={e => {
          setInviteForm({ ...inviteForm, role: e.target.value });
          setSentBanner(null);
        }}
        placeholder="e.g., Voice Artist"
        style={inputStyle}
      />

      <label style={labelStyle}>Message</label>
      <textarea
        value={inviteForm.message}
        onChange={e => {
          setInviteForm({ ...inviteForm, message: e.target.value });
          setSentBanner(null);
        }}
        placeholder="Optional message to explain the opportunity"
        style={{ ...inputStyle, height: '80px' }}
      />

      {/* Agreement Section */}
      <div style={{ marginTop: '20px', fontSize: '13px' }}>
        <h5 style={{ color: colors.textSecondary, marginBottom: '8px' }}>🔐 Collaboration Agreement</h5>
        <p style={{ marginBottom: '8px' }}>
          Before sending an invite, please agree to the
          <a href="/legal/nda.html" target="_blank" style={{ color: colors.accent, margin: '0 6px' }}>Mutual NDA</a> and
          <a href="/legal/terms.html" target="_blank" style={{ color: colors.accent, marginLeft: '6px' }}>Terms & Conditions</a>.
        </p>
        <label>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => { setAgreed(e.target.checked); setSentBanner(null); }}
          />{' '}
          I have read and agree to the NDA and Terms
        </label>
      </div>

      <div style={{ marginTop: '20px' }}>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: colors.accent,
            opacity: (agreed && !guardBlocked && !!inviteForm.project) ? 1 : 0.6,
            cursor: (agreed && !guardBlocked && !!inviteForm.project) ? 'pointer' : 'not-allowed'
          }}
          onClick={sendInvite}
          disabled={sending || !agreed || guardBlocked || !inviteForm.project}
          title={guardBlocked ? 'This project is already assigned to another collaborator.' : undefined}
        >
          {sending ? 'Sending...' : '📩 Send Invite'}
        </button>
      </div>
    </div>
  );
};

export default SendInvite;
